import { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/logo.png';

function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showNavbar, setShowNavbar] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isResourcesOpen, setIsResourcesOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const resourcesRef = useRef(null);
  const userDropdownRef = useRef(null);

  const handleLogout = () => {
    logout();
    navigate('/');
    setIsMenuOpen(false);
    setIsUserDropdownOpen(false);
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    // Menü açıkken body scroll'u engelle
    if (!isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  };

  const toggleResources = () => {
    setIsResourcesOpen(!isResourcesOpen);
  };

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  // Role göre dashboard yönlendirmesi
  const getDashboardPath = () => {
    if (!user) return '/';
    switch(user.role) {
      case 'admin':
        return '/admin';
      case 'teacher':
        return '/teacher';
      case 'student':
        return '/student';
      default:
        return '/';
    }
  };

  // Role göre profil yönlendirmesi
  const getProfilePath = () => {
    if (!user) return '/';
    switch(user.role) {
      case 'admin':
        return '/admin/settings';
      case 'teacher':
        return '/teacher/settings';
      case 'student':
        return '/student/settings';
      default:
        return '/';
    }
  };

  // Dışarı tıklamada dropdown'ları kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (resourcesRef.current && !resourcesRef.current.contains(event.target)) {
        setIsResourcesOpen(false);
      }
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Scroll behavior
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Sadece desktopta scroll hide/show yap
      if (window.innerWidth > 968) {
        if (window.scrollY > lastScrollY && window.scrollY > 100) {
          setShowNavbar(false); // scroll down
        } else {
          setShowNavbar(true); // scroll up
        }
      } else {
        setShowNavbar(true); // mobilde her zaman görünür
      }
      setLastScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  // Sayfa değişiminde menüleri kapat
  useEffect(() => {
    setIsMenuOpen(false);
    setIsResourcesOpen(false);
    setIsUserDropdownOpen(false);
    document.body.style.overflow = 'unset';
  }, [location]);

  return (
    <nav className={`navbar ${isScrolled ? 'scrolled' : ''} ${showNavbar ? 'navbar--visible' : 'navbar--hidden'}`}>
      <div className="navbar-container">
        <div className="navbar-logo">
          <Link to="/">
            <img src={logo} alt="Adam Academy Logo" className="logo-img" />
          </Link>
        </div>

        <ul className={`navbar-links ${isMenuOpen ? 'active' : ''}`}>
          <li>
            <Link 
              to="/" 
              className={location.pathname === '/' ? 'active' : ''}
              onClick={() => setIsMenuOpen(false)}
            >
              HOME
            </Link>
          </li>
          <li>
            <Link 
              to="/about" 
              className={location.pathname === '/about' ? 'active' : ''}
              onClick={() => setIsMenuOpen(false)}
            >
              ABOUT
            </Link>
          </li>
          <li>
            <Link 
              to="/programs" 
              className={location.pathname === '/programs' ? 'active' : ''}
              onClick={() => setIsMenuOpen(false)}
            >
              PROGRAMS
            </Link>
          </li>
          <li>
            <Link 
              to="/academic-staff" 
              className={location.pathname === '/academic-staff' ? 'active' : ''}
              onClick={() => setIsMenuOpen(false)}
            >
              ACADEMIC STAFF
            </Link>
          </li>
          <li>
            <Link 
              to="/news" 
              className={location.pathname === '/news' ? 'active' : ''}
              onClick={() => setIsMenuOpen(false)}
            >
              EVENTS
            </Link>
          </li>
          
          {/* Resources Dropdown */}
          <li className="navbar-resources" ref={resourcesRef}>
            <button 
              className={`resources-toggle ${isResourcesOpen ? 'active' : ''}`}
              onClick={toggleResources}
              aria-expanded={isResourcesOpen}
            >
              RESOURCES
              <span className={`dropdown-arrow ${isResourcesOpen ? 'open' : ''}`}>▼</span>
            </button>
            
            <div className={`resources-dropdown ${isResourcesOpen ? 'active' : ''}`}>
              <Link to="/products" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                OUR PRODUCTS
              </Link>
              <Link to="/online-courses" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                ONLINE COURSES
              </Link>
              <Link to="/downloads" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                DOWNLOADS
              </Link>
              <Link to="/partners" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                PARTNERS WITH ADAM
              </Link>
              <Link to="/references" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                REFERENCES & ARTICLES
              </Link>
              <Link to="/research-development" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                R&D
              </Link>
              <Link to="/technology" className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                TECHNOLOGY
              </Link>
            </div>
          </li>

          {/* User Section */}
          {user ? (
            <li className="navbar-user" ref={userDropdownRef}>
              <button 
                className={`user-toggle ${isUserDropdownOpen ? 'active' : ''}`}
                onClick={toggleUserDropdown}
                aria-expanded={isUserDropdownOpen}
              >
                <span className="user-avatar">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </span>
                <span className="user-greeting">{user.name || 'User'}</span>
                <span className={`dropdown-arrow ${isUserDropdownOpen ? 'open' : ''}`}>▼</span>
              </button>
              
              <div className={`user-dropdown ${isUserDropdownOpen ? 'active' : ''}`}>
                {/* Role-based Dashboard Link */}
                <Link to={getDashboardPath()} className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                  Dashboard
                </Link>
                
                {/* Role-based Profile Link */}
                <Link to={getProfilePath()} className="dropdown-item" onClick={() => setIsMenuOpen(false)}>
                  My Profile
                </Link>
                
                <div className="dropdown-divider"></div>
                <button onClick={handleLogout} className="dropdown-item logout-btn">
                  Logout
                </button>
              </div>
            </li>
          ) : (
            <li className="navbar-auth">
              <Link 
                to="/login" 
                className="auth-button join-us-btn"
                onClick={() => setIsMenuOpen(false)}
              >
                JOIN US
              </Link>
            </li>
          )}
        </ul>

        <button 
          className={`navbar-toggle ${isMenuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          aria-label="Toggle menu"
          aria-expanded={isMenuOpen}
        >
          <span></span>
          <span></span>
          <span></span>
        </button>
      </div>
    </nav>
  );
}

export default Navbar;